import { useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { ReceiptPicker } from '../expenses/ReceiptPicker';
import { MAX_ADJUNTOS_NOVEDAD } from '../../types/logbook';
import { formatearPeso } from '../../utils/format';

interface Props {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  /** Cuántas caben todavía. Por defecto, el tope entero (novedad nueva, sin fotos). */
  cupo?: number;
}

/**
 * FE-30 — elegir VARIAS fotos para una novedad.
 *
 * **Envuelve `ReceiptPicker` en vez de reescribirlo.** Ese componente ya
 * resolvió lo difícil en `FE-12`: los dos inputs —porque `capture="environment"`
 * SUSTITUYE al selector de archivos en Chrome Android y Safari iOS, así que con
 * uno solo o no abres la cámara de un toque o no puedes elegir un PDF ya
 * escaneado—, la compresión al elegir, y el `e.target.value = ''` sin el cual
 * volver a elegir el mismo archivo no dispara `change`.
 *
 * Lo único que este componente añade es la lista: el hijo sigue gestionando UN
 * archivo, y en cuanto entra se mueve al array y el hijo vuelve a estar vacío,
 * listo para el siguiente. Esa ranura vacía ES el botón de "añadir otra".
 *
 * **El tope se aplica aquí además de en el servidor.** El 409 llega igual, pero
 * enterarte después de subir 10 MiB desde la carretera no es enterarte a tiempo.
 */
export function LogbookPhotoPicker({ files, onChange, disabled = false, cupo = MAX_ADJUNTOS_NOVEDAD }: Props) {
  // El hijo es una ranura de entrada, no un estado: lo que elige pasa
  // inmediatamente al array y él se vacía.
  const [enCurso, setEnCurso] = useState<File | null>(null);

  const restantes = Math.max(0, cupo - files.length);

  const recibir = (f: File | null) => {
    if (!f) { setEnCurso(null); return; }
    onChange([...files, f]);
    setEnCurso(null);
  };

  const quitar = (i: number) => onChange(files.filter((_, j) => j !== i));

  return (
    <div>
      {files.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0 0 0.625rem', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {files.map((f, i) => (
            <li
              key={`${f.name}-${f.lastModified}-${i}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.625rem',
                padding: '0.5rem 0.625rem', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)', background: 'var(--bg-raised)',
              }}
            >
              <ImagePlus size={15} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '12.5px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.name}
                </p>
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{formatearPeso(f.size)}</p>
              </div>
              <button
                type="button"
                onClick={() => quitar(i)}
                disabled={disabled}
                aria-label={`Quitar ${f.name}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '44px', height: '44px', flexShrink: 0,
                  border: 'none', background: 'transparent', color: '#EF4444',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Trash2 size={15} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {restantes > 0 ? (
        <ReceiptPicker
          file={enCurso}
          onChange={recibir}
          disabled={disabled}
          label={files.length === 0 ? 'Fotos de la novedad' : 'Añadir otra foto'}
          ocultarOpcional={files.length > 0}
        />
      ) : (
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)' }}>
          Ya son {cupo} fotos, que es el máximo. Una foto no se puede borrar después de subirla.
        </p>
      )}
    </div>
  );
}
